"use client";
import { useRef, useEffect } from "react";
import { gsap } from "@/lib/gsap";
import AnimatedStats from "./AnimatedStats";

interface StatItem {
  target: number;
  suffix: string;
  prefix?: string;
  label: string;
}

interface AboutStatsData {
  tagline?: string;
  description?: string;
  stats: StatItem[];
  title?: string;
}

export default function AboutStats({ data }: { data: AboutStatsData }) {
  const { tagline, description, stats, title } = data;

  const sectionRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLHRElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          end: "top 50%",
          toggleActions: "play none none reverse",
        },
      });

      timeline
        .fromTo(
          headingRef.current,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
          }
        )
        .fromTo(
          titleRef.current,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.6"
        )
        .fromTo(
          descriptionRef.current,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.4"
        )
        .fromTo(
          lineRef.current,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power2.out",
          },
          "-=0.4"
        );
      gsap.fromTo(
        cardRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top 75%",
            end: "top 45%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-[70px]">
      <div className="container">
        <div className="flex flex-col lg:flex-row xl:gap-x-8 lg:gap-x-5 gap-x-8">
          <div className="lg:min-w-[11.6%] min-w-full max-lg:mb-4">
            {tagline && (
              <div
                ref={headingRef}
                className="flex items-center justify-center lg:justify-start  text-center lg:text-left  opacity-0"
              >
                <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
                <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
                  {tagline}
                </p>
              </div>
            )}
            {title && (
              <h2
                ref={titleRef}
                className=" text-4xl lg:text-[29px] xl:text-4xl  font-bold text-theme-secondary capitalize mt-3 text-center lg:text-left opacity-0"
              >
                {title}
              </h2>
            )}
          </div>
          {description && (
            <div
              ref={descriptionRef}
              className="lg:w-[78%] w-full text-center lg:text-left opacity-0"
            >
              <p className="text-xl lg:text-3xl xl:text-4xl text-primary  leading-tight font-medium">
                {description}
              </p>
            </div>
          )}
        </div>
        <hr
          ref={lineRef}
          className="border-[#EFF0F0] mt-6 md:mt-10 h-px w-full opacity-0"
        />
        <div
          ref={cardRef}
          className="lg:w-[88.4%] w-full ms-auto md:pt-[33px] pt-4 min-h-[200px] opacity-0"
        >
          <AnimatedStats stats={stats} />
        </div>
      </div>
    </section>
  );
}
